<template>
  <div class="progress-monitor">
    <h2>Student Progress Monitor</h2>
    
    <!-- User Selection -->
    <ion-item>
      <ion-select label="User" label-placement="stacked" placeholder="Select User" v-model="selectedUserId" @ionChange="loadUserExercises">
        <ion-select-option v-for="user in users" :key="user.id" :value="user.id">
          {{ getUserDisplayName(user) }}
        </ion-select-option>
      </ion-select>
    </ion-item>
    
    <!-- Admin debug toggle -->
    <div class="debug-toggle" v-if="isAdmin">
      <ion-button size="small" fill="clear" @click="showDebugConsole = !showDebugConsole">
        {{ showDebugConsole ? 'Hide Debug Info' : 'Show Debug Info' }}
      </ion-button>
    </div>
    
    <!-- Admin debug console -->
    <ion-card v-if="showDebugConsole && exerciseProgress">
      <ion-card-header>
        <ion-card-title>Debug Information</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <p><strong>Document Structure:</strong></p>
        <pre class="debug-console">{{ JSON.stringify(exerciseProgress, null, 2) }}</pre>
      </ion-card-content>
    </ion-card>
    
    <!-- Loading Indicator -->
    <div v-if="isLoading" class="loading-container">
      <ion-spinner name="circles"></ion-spinner>
      <p>Loading data...</p>
    </div>
    
    <!-- Exercise Selection (displayed after user selection) -->
    <ion-item v-if="selectedUserId && !isLoading">
      <ion-select label="Exercise" label-placement="stacked" placeholder="Select Exercise" v-model="selectedExerciseId" @ionChange="loadExerciseProgress">
        <ion-select-option v-for="exercise in userExercises" :key="exercise.id" :value="exercise.id">
          {{ exercise.name }} - {{ formatDate(exercise.lastUpdated) }}
        </ion-select-option>
      </ion-select>
    </ion-item>
    
    <!-- No exercises message -->
    <ion-card v-if="selectedUserId && userExercises.length === 0 && !isLoading">
      <ion-card-content>
        <p>No exercises found for this user.</p>
      </ion-card-content>
    </ion-card>
    
    <!-- Progress Display (after exercise selection) -->
    <div v-if="selectedExerciseId && exerciseProgress && !isLoading">
      <!-- Overall progress information -->
      <ion-card>
        <ion-card-header>
          <ion-card-title>{{ exerciseProgress.exercise.topic }}</ion-card-title>
          <ion-card-subtitle>Goal: {{ exerciseProgress.exercise.goal }}</ion-card-subtitle>
        </ion-card-header>
        <ion-card-content>
          <p><strong>Current Mode:</strong> {{ exerciseProgress.mode }}</p>
          <p><strong>Last Updated:</strong> {{ formatDate(exerciseProgress.lastUpdated) }}</p>
        </ion-card-content>
      </ion-card>
      
      <!-- Progress History -->
      <ion-card v-if="exerciseProgress?.progressHistory?.items?.length > 0">
        <ion-card-header>
          <ion-card-title>Progress History</ion-card-title>
          <ion-card-subtitle>{{ exerciseProgress.progressHistory.items.length }} sessions recorded</ion-card-subtitle>
        </ion-card-header>
        <ion-card-content>
          <!-- Simple expandable sections instead of ion-accordion -->
          <div v-for="(historyItem, index) in exerciseProgress.progressHistory.items" :key="index" class="history-item">
            <div class="history-header" @click="toggleHistoryItem(index)">
              <div class="header-content">
                <span class="date">{{ formatDate(historyItem.timestamp) }}</span>
                <span class="mode">{{ historyItem.mode }}</span>
                <span class="score-badge" v-if="historyItem.feedback?.overallScore !== undefined">
                  Score: {{ historyItem.feedback.overallScore }}%
                </span>
              </div>
              <ion-icon :icon="expandedItems[index] ? chevronUpOutline : chevronDownOutline" class="toggle-icon"></ion-icon>
            </div>
            
            <!-- Content section (expanded/collapsed) -->
            <div v-if="expandedItems[index]" class="history-content">
              <!-- Milestone information -->
              <h4>Milestone: {{ historyItem.milestone?.goal || 'No milestone goal available' }}</h4>
              <ul v-if="historyItem.milestone?.items?.length > 0">
                <li v-for="(objective, i) in historyItem.milestone.items" :key="i">
                  <strong>{{ objective.objectives }}</strong>
                  <p>{{ objective.scope }}</p>
                </li>
              </ul>
              <p v-else>No milestone items available</p>
              
              <!-- Feedback information -->
              <h4>Overall Feedback</h4>
              <div class="overall-feedback">
                <p v-if="historyItem.feedback?.overallPerformance">
                  <strong>Summary:</strong> {{ historyItem.feedback.overallPerformance }}
                </p>
                <p v-else>No overall performance summary available</p>
                
                <p v-if="historyItem.feedback?.overallScore !== undefined">
                  <strong>Overall Achievement:</strong> 
                  <span class="overall-score" :style="{ backgroundColor: getScoreColor(historyItem.feedback.overallScore) }">
                    {{ historyItem.feedback.overallScore }}%
                  </span>
                </p>
                <p v-else>No overall score available</p>
              </div>
              
              <!-- Detailed Feedback Items -->
              <h4>Objective Feedback</h4>
              <div v-if="historyItem.feedback?.items?.length > 0" class="objectives-grid">
                <div v-for="(feedbackItem, j) in historyItem.feedback.items" :key="j" class="objective-card">
                  <h6>{{ feedbackItem.objective }}</h6>
                  <div class="score-indicator" :style="{ backgroundColor: getScoreColor(feedbackItem.acheivementScore) }">
                    {{ feedbackItem.acheivementScore }}%
                  </div>
                  <div class="objective-details">
                    <div class="strengths">
                      <strong>Strengths:</strong> 
                      <p>{{ feedbackItem.userIsGoodAt }}</p>
                    </div>
                    <div class="improvements">
                      <strong>Areas for Improvement:</strong>
                      <p>{{ feedbackItem.userCanImprove }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <p v-else>No feedback details available for objectives</p>
            </div>
          </div>
        </ion-card-content>
      </ion-card>
      
      <!-- No progress history message -->
      <ion-card v-else-if="exerciseProgress">
        <ion-card-content>
          <p>No progress history available for this exercise.</p>
        </ion-card-content>
      </ion-card>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { 
  IonItem, IonSelect, IonSelectOption, IonCard, IonCardHeader, 
  IonCardTitle, IonCardSubtitle, IonCardContent, 
  IonSpinner, IonButton, IonIcon 
} from '@ionic/vue';
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons';

export default {
  name: 'ProgressMonitor',
  components: {
    IonItem, IonSelect, IonSelectOption, IonCard, IonCardHeader, 
    IonCardTitle, IonCardSubtitle, IonCardContent, 
    IonSpinner, IonButton, IonIcon
  },
  setup() {
    const store = useStore();
    const isAdmin = computed(() => store.getters['account/isAdmin']);
    const users = ref([]);
    const selectedUserId = ref('');
    const userExercises = ref([]);
    const selectedExerciseId = ref('');
    const exerciseProgress = ref(null);
    const isLoading = ref(false);
    const showDebugConsole = ref(false);
    const expandedItems = ref({});

    // Toggle expand/collapse for history items
    const toggleHistoryItem = (index) => {
      expandedItems.value = {
        ...expandedItems.value,
        [index]: !expandedItems.value[index]
      };
    };

    // Load users list (admin only)
    const loadUsers = async () => {
      if (isAdmin.value) {
        try {
          const usersCollection = collection(db, 'users');
          const usersSnapshot = await getDocs(usersCollection);
          users.value = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log(`Loaded ${users.value.length} users`);
        } catch (error) {
          console.error("Error loading users:", error);
        }
      }
    };

    // Get user display name
    const getUserDisplayName = (user) => {
      // Custom mapping for specific users
      const userMap = {
        "JhpWuePl4jOLHwi3Shm15mKlxFG3": "ladan.tania@gmail.com",
        "BCCtXDrAzoRfjsVN8c3CwfjAxJv1": "olgabond.sr@gmail.com",
        "OFolMrbtxfTnrWm9zfQTrejTFpY2": "olgabond.dec@gmail.com",
        "g6wBjE32ZdQSFw0lQ7pe5PMWcAS2": "al18po05priv@gmail.com"
      };
      
      if (user.id in userMap) {
        return userMap[user.id];
      }
      return user.name || user.email || user.id || 'Unknown User';
    };

    // Load exercises for selected user
    const loadUserExercises = async () => {
      if (!selectedUserId.value) return;
      
      isLoading.value = true;
      selectedExerciseId.value = ''; // Reset exercise selection
      exerciseProgress.value = null; // Reset progress data
      userExercises.value = []; // Clear existing exercises
      
      try {
        console.log(`Loading exercises for user: ${selectedUserId.value}`);
        
        // Query user_exercise_state documents for the selected user
        const stateRef = collection(db, 'user_exercise_state');
        
        // The document IDs are in the format "userId:exerciseId"
        // We can use startAt and endAt to find all documents for this user
        const userPrefix = `${selectedUserId.value}:`;
        const userPrefixEnd = `${selectedUserId.value}:\uf8ff`; // High Unicode value
        
        const stateQuery = query(
          stateRef,
          where('__name__', '>=', userPrefix), 
          where('__name__', '<=', userPrefixEnd)
        );
        
        let stateSnapshot = await getDocs(stateQuery);
        console.log(`Found ${stateSnapshot.docs.length} state documents for user`);
        
        if (stateSnapshot.empty) {
          // As a fallback, try the original query method
          const fallbackQuery = query(
            stateRef,
            where('userId', '==', selectedUserId.value)
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          console.log(`Fallback query found ${fallbackSnapshot.docs.length} state documents`);
          
          if (!fallbackSnapshot.empty) {
            stateSnapshot = fallbackSnapshot;
          }
        }
        
        // Transform the data to include exercise details
        const exercises = await Promise.all(stateSnapshot.docs.map(async (docSnapshot) => {
          const docId = docSnapshot.id;
          console.log(`Processing document: ${docId}`);
          
          const data = docSnapshot.data();
          console.log("Document data:", data);
          
          // Extract exerciseId from document ID if it's in the format userId:exerciseId
          let exerciseId;
          if (docId.includes(':')) {
            exerciseId = docId.split(':')[1];
          } else if (data.exercise && data.exercise.id) {
            exerciseId = data.exercise.id;
          } else {
            console.warn(`Cannot determine exerciseId for document ${docId}`);
            return null;
          }
          
          // Get the exercise details from the exercises collection
          const exerciseDoc = await getDoc(doc(db, 'exercises', exerciseId));
          const exerciseData = exerciseDoc.exists() ? exerciseDoc.data() : {};
          
          let topic = '';
          let lastUpdated = new Date();
          
          // Extract topic from data if it exists
          if (data.exercise && data.exercise.topic) {
            topic = data.exercise.topic;
          } else if (exerciseData.topic) {
            topic = exerciseData.topic;
          }
          
          // Extract lastUpdated timestamp if it exists
          if (data.lastUpdated) {
            if (typeof data.lastUpdated === 'string') {
              lastUpdated = new Date(data.lastUpdated);
            } else if (data.lastUpdated.toDate) {
              // Handle Firestore Timestamp
              lastUpdated = data.lastUpdated.toDate();
            }
          }
          
          return {
            id: exerciseId,
            name: exerciseData.name || topic || 'Unknown Exercise',
            topic: topic,
            lastUpdated: lastUpdated,
            documentId: docId // Store the full document ID for later use
          };
        }));
        
        // Filter out null entries and sort by lastUpdated
        const validExercises = exercises.filter(ex => ex !== null);
        console.log(`Found ${validExercises.length} valid exercises`);
        
        // Sort exercises by lastUpdated in descending order (newest first)
        userExercises.value = validExercises.sort((a, b) => 
          b.lastUpdated.getTime() - a.lastUpdated.getTime()
        );
      } catch (error) {
        console.error("Error loading user exercises:", error);
      } finally {
        isLoading.value = false;
      }
    };

    // Load progress for selected exercise
    const loadExerciseProgress = async () => {
      if (!selectedUserId.value || !selectedExerciseId.value) return;
      
      isLoading.value = true;
      exerciseProgress.value = null;
      expandedItems.value = {}; // Reset expanded items
      
      try {
        // Find the selected exercise in our userExercises array
        const selectedExercise = userExercises.value.find(ex => ex.id === selectedExerciseId.value);
        if (!selectedExercise) {
          console.error("Selected exercise not found in list");
          return;
        }
        
        // Use the document ID if available, otherwise construct it
        const docId = selectedExercise.documentId || `${selectedUserId.value}:${selectedExerciseId.value}`;
        console.log(`Loading document: ${docId}`);
        
        const docRef = doc(db, 'user_exercise_state', docId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Document data:", data);
          
          // Deep copy to avoid mutation issues
          const processedData = JSON.parse(JSON.stringify(data));
          
          // Process lastUpdated timestamp
          if (data.lastUpdated) {
            if (typeof data.lastUpdated === 'string') {
              processedData.lastUpdated = new Date(data.lastUpdated);
            } else if (data.lastUpdated.toDate) {
              processedData.lastUpdated = data.lastUpdated.toDate();
            } else {
              processedData.lastUpdated = new Date();
            }
          } else {
            processedData.lastUpdated = new Date();
          }
          
          // Process progress history if it exists
          if (data.progressHistory && data.progressHistory.items) {
            console.log("Progress history found with items:", data.progressHistory.items);
            
            const processedItems = data.progressHistory.items.map(item => {
              // Create a deep copy to avoid modifying the original
              const processedItem = JSON.parse(JSON.stringify(item));
              
              // Convert timestamp
              if (item.timestamp) {
                if (typeof item.timestamp === 'string') {
                  processedItem.timestamp = new Date(item.timestamp);
                } else if (item.timestamp.toDate) {
                  processedItem.timestamp = item.timestamp.toDate();
                } else {
                  processedItem.timestamp = new Date();
                }
              } else {
                processedItem.timestamp = new Date();
              }
              
              // Ensure feedback objects are properly structured
              if (!processedItem.feedback) {
                processedItem.feedback = {
                  items: [],
                  overallPerformance: 'No feedback available',
                  overallScore: 0
                };
              } else if (!Array.isArray(processedItem.feedback.items)) {
                processedItem.feedback.items = [];
              }
              
              return processedItem;
            });
            
            // Add sorted and processed items to the progress history
            processedData.progressHistory = {
              items: processedItems.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
            };
            
            console.log("Processed progress history:", processedData.progressHistory);
          } else {
            processedData.progressHistory = { items: [] };
            console.log("No progress history found in document");
          }
          
          exerciseProgress.value = processedData;
          
          // Automatically expand the first history item if any exist
          if (processedData.progressHistory.items.length > 0) {
            expandedItems.value[0] = true;
          }
        } else {
          console.log(`No document found with ID: ${docId}`);
          // Try an alternative approach by querying with where clause
          const stateRef = collection(db, 'user_exercise_state');
          const stateQuery = query(
            stateRef,
            where('userId', '==', selectedUserId.value),
            where('exercise.id', '==', selectedExerciseId.value)
          );
          
          const querySnapshot = await getDocs(stateQuery);
          if (!querySnapshot.empty) {
            const data = querySnapshot.docs[0].data();
            console.log("Found via query:", data);
            
            // Deep copy to avoid mutation issues
            const processedData = JSON.parse(JSON.stringify(data));
            
            // Process document data
            if (data.lastUpdated) {
              if (typeof data.lastUpdated === 'string') {
                processedData.lastUpdated = new Date(data.lastUpdated);
              } else if (data.lastUpdated.toDate) {
                processedData.lastUpdated = data.lastUpdated.toDate();
              } else {
                processedData.lastUpdated = new Date();
              }
            } else {
              processedData.lastUpdated = new Date();
            }
            
            // Process progress history if it exists
            if (data.progressHistory && data.progressHistory.items) {
              console.log("Progress history found via query:", data.progressHistory.items);
              
              const processedItems = data.progressHistory.items.map(item => {
                // Create a deep copy to avoid modifying the original
                const processedItem = JSON.parse(JSON.stringify(item));
                
                // Convert timestamp
                if (item.timestamp) {
                  if (typeof item.timestamp === 'string') {
                    processedItem.timestamp = new Date(item.timestamp);
                  } else if (item.timestamp.toDate) {
                    processedItem.timestamp = item.timestamp.toDate();
                  } else {
                    processedItem.timestamp = new Date();
                  }
                } else {
                  processedItem.timestamp = new Date();
                }
                
                // Ensure feedback objects are properly structured
                if (!processedItem.feedback) {
                  processedItem.feedback = {
                    items: [],
                    overallPerformance: 'No feedback available',
                    overallScore: 0
                  };
                } else if (!Array.isArray(processedItem.feedback.items)) {
                  processedItem.feedback.items = [];
                }
                
                return processedItem;
              });
              
              // Add sorted and processed items to the progress history
              processedData.progressHistory = {
                items: processedItems.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
              };
              
              // Automatically expand the first history item if any exist
              if (processedItems.length > 0) {
                expandedItems.value[0] = true;
              }
            } else {
              processedData.progressHistory = { items: [] };
            }
            
            exerciseProgress.value = processedData;
          } else {
            console.log("No progress data found via query either");
          }
        }
        
        // Additional debugging for items
        if (exerciseProgress.value?.progressHistory?.items) {
          console.log("Progress history items count:", exerciseProgress.value.progressHistory.items.length);
          
          // Additional debugging for each item
          exerciseProgress.value.progressHistory.items.forEach((item, index) => {
            console.log(`Processing history item ${index}:`, item);
            if (item.feedback) {
              console.log(`Item ${index} has feedback:`, item.feedback);
              if (item.feedback.items) {
                console.log(`Item ${index} has ${item.feedback.items.length} feedback items`);
              } else {
                console.warn(`Item ${index} feedback.items is missing`);
                item.feedback.items = [];
              }
            } else {
              console.warn(`Item ${index} has no feedback object`);
              item.feedback = {
                items: [],
                overallPerformance: 'No feedback available',
                overallScore: 0
              };
            }
          });
        }
      } catch (error) {
        console.error("Error loading exercise progress:", error);
      } finally {
        isLoading.value = false;
      }
    };

    // Get score color based on achievement percentage
    const getScoreColor = (score) => {
      if (score >= 90) return '#4caf50'; // Green
      if (score >= 70) return '#8bc34a'; // Light green
      if (score >= 50) return '#ffeb3b'; // Yellow
      return '#ff9800'; // Orange
    };

    // Format date helper
    const formatDate = (date, short = false) => {
      if (!date) return 'Unknown';
      if (typeof date === 'string') {
        date = new Date(date);
      }
      
      if (short) {
        return date.toLocaleDateString();
      }
      return date.toLocaleString();
    };

    onMounted(async () => {
      if (isAdmin.value) {
        await loadUsers();
      }
    });

    return {
      isAdmin,
      users,
      selectedUserId,
      userExercises,
      selectedExerciseId,
      exerciseProgress,
      isLoading,
      showDebugConsole,
      expandedItems,
      chevronDownOutline,
      chevronUpOutline,
      loadUsers,
      getUserDisplayName,
      loadUserExercises,
      loadExerciseProgress,
      formatDate,
      getScoreColor,
      toggleHistoryItem
    };
  }
};
</script>

<style scoped>
.progress-monitor {
  padding: 16px;
}

h2 {
  margin-bottom: 20px;
}

ion-card {
  margin-bottom: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

ul {
  padding-left: 20px;
}

h4 {
  margin-top: 20px;
  margin-bottom: 10px;
  color: var(--ion-color-primary);
}

h5 {
  margin-bottom: 8px;
  color: var(--ion-color-dark);
}

h6 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: var(--ion-color-dark);
}

.objectives-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.overall-feedback {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.overall-score {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 16px;
  color: white;
  font-weight: bold;
  margin-left: 8px;
}

.objective-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  background-color: #fafafa;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.objective-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.score-indicator {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 16px;
  color: white;
  font-weight: bold;
  margin-bottom: 12px;
}

.objective-details {
  font-size: 14px;
}

.strengths {
  margin-bottom: 10px;
  border-left: 3px solid #4caf50;
  padding-left: 10px;
}

.improvements {
  border-left: 3px solid #ff9800;
  padding-left: 10px;
}

.strengths strong, .improvements strong {
  color: var(--ion-color-dark);
  display: block;
  margin-bottom: 4px;
}

.strengths p, .improvements p {
  margin: 5px 0 0 0;
}

.score-badge {
  margin-left: 10px;
  padding: 2px 8px;
  background-color: var(--ion-color-primary);
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 40px 0;
}

.loading-container p {
  margin-top: 16px;
  color: var(--ion-color-medium);
  font-size: 16px;
}

.debug-toggle {
  margin: 8px 0;
  display: flex;
  justify-content: flex-end;
}

.debug-console {
  max-height: 400px;
  overflow: auto;
  background-color: #f0f0f0;
  padding: 10px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Custom styles for expandable items */
.history-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f9f9f9;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.history-header:hover {
  background-color: #f0f0f0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.date {
  font-weight: 500;
}

.mode {
  font-style: italic;
  color: var(--ion-color-medium);
}

.toggle-icon {
  font-size: 20px;
  color: var(--ion-color-medium);
  transition: transform 0.3s ease;
}

.history-content {
  padding: 16px;
  background-color: white;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>